/// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';
    
    interface IBlockTypesByLevelMap {
        [key: string]: Models.BlockType[];
    }
    
    interface ILevelByBlockTypeMap {
        [key: string]: string[];
    }
    
    /**
     * This class contains a cache of blockTypes -> Level relation.
     * It could be used to review the different caches (by level and by blockType),
     * but the most useful methods are:
     * - filterByInputLevel
     * - filterByOutputLevel
     * These two methods take as argument a list of blocks and a level and return
     * a list of blocks that have that level as input or output.
     * Other two useful methods are:
     * - extractCurrentInputLevels
     * - extractCurrentOutputLevels
     * They return a list of levels in the blocks passed as argument (without duplicates).
     * This way we can show only the labels available in the blocks at the current room.
     */
    export class LevelMaps {
        private static levels: string[] = ["ASI", "SDI", "TSoIP"]; // , "FILE", "Ikusnet"];
        public blockTypesByInputLevelMap: IBlockTypesByLevelMap = {};
        public blockTypesByOutputLevelMap: IBlockTypesByLevelMap = {};
        private inputLevelByBlockTypeMap: ILevelByBlockTypeMap = {};
        private outputLevelByBlockTypeMap: ILevelByBlockTypeMap = {};
        private blockService: Services.BlockService;
        
        constructor(blockService: Services.BlockService) {
            this.blockService = blockService;
            this.initLevelMaps();
        }
        
        public refresh(): void {
            this.initLevelMaps();
        }
        
        public filterByInputLevel(blocks: Models.IBlock[], level: string): Models.IBlock[] {
            return blocks.filter((block: Models.IBlock) => {
                let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(block.blockType.href);
                let blockLevels: string[] = this.inputLevelByBlockTypeMap[simplifiedHref];
                return blockLevels && blockLevels.some((aLevel: string) => {
                    return aLevel === level;
                });
            });
        }
        
        public filterByOutputLevel(blocks: Models.IBlock[], level: string): Models.IBlock[] {
            return blocks.filter((block: Models.IBlock) => {
                let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(block.blockType.href);
                let blockLevels: string[] = this.outputLevelByBlockTypeMap[simplifiedHref];
                return blockLevels && blockLevels.some((aLevel: string) => {
                    return aLevel === level;
                });
            });
        }
        
        public extractCurrentInputLevels(sourceBlocks: Models.IBlock[]): string[] {
            let levels: {} = {};
            if (sourceBlocks) {
                sourceBlocks.forEach((block: Models.IBlock) => {
                    let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(block.blockType.href);
                    if (this.inputLevelByBlockTypeMap[simplifiedHref] !== undefined) {
                        this.inputLevelByBlockTypeMap[simplifiedHref].forEach((level: string) => {
                            levels[level] = true;
                        });
                    }
                });
            }        
            return Object.keys(levels).sort(this.levelComparator);
        }
        
        public extractCurrentOutputLevels(sinkBlocks: Models.IBlock[]): string[] {
            let levels: {} = {};
            sinkBlocks.forEach((block: Models.IBlock) => {
                let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(block.blockType.href);
                const outputLevelMap: string[] = this.outputLevelByBlockTypeMap[simplifiedHref];
                // temporary fix to cope with blocks without output connections
                if (outputLevelMap) {
                    outputLevelMap.forEach((level: string) => {
                        levels[level] = true;
                    });
                } else {
                    this.outputLevelByBlockTypeMap[simplifiedHref] = ['SDI'];
                    let sdiString: string = 'SDI';
                    levels[sdiString] = true;
                }
            });
            return Object.keys(levels).sort(this.levelComparator);
        }
        
        private initLevelMaps(): void {
            this.blockTypesByInputLevelMap = {};
            this.blockTypesByOutputLevelMap = {};
            this.inputLevelByBlockTypeMap = {};
            this.outputLevelByBlockTypeMap = {};
            LevelMaps.levels.forEach((level: string) => {
                this.blockService.findBlockTypesByInputLevel(level).then( (types: Models.BlockType[]) => {
                    types.forEach((type: Models.BlockType) => {
                        let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(type.href); 
                        if (!this.inputLevelByBlockTypeMap[simplifiedHref]) {
                            this.inputLevelByBlockTypeMap[simplifiedHref] = [];
                        }
                        this.inputLevelByBlockTypeMap[simplifiedHref].push(level);
                        if (!this.blockTypesByInputLevelMap[level]) {
                            this.blockTypesByInputLevelMap[level] = [];
                        }
                        this.blockTypesByInputLevelMap[level].push(type);
                    });
                });
                this.blockService.findBlockTypesByOutputLevel(level).then( (types: Models.BlockType[]) => {
                    types.forEach((type: Models.BlockType) => {
                        let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(type.href);
                        if (!this.outputLevelByBlockTypeMap[simplifiedHref]) {
                            this.outputLevelByBlockTypeMap[simplifiedHref] = [];
                        }
                        this.outputLevelByBlockTypeMap[simplifiedHref].push(level);
                        if (!this.blockTypesByOutputLevelMap[level]) {
                            this.blockTypesByOutputLevelMap[level] = [];
                        }
                        this.blockTypesByOutputLevelMap[level].push(type);
                    });
                });
            });

        }
        
        private levelComparator(a: string, b: string): number {
            if (a === 'SDI') {
                return -1;
            } else if (b === 'SDI') {
                return 1;
            } else {
                if (a < b)
                    return -1;
                else return 1;
            }
        }
        
    }
}






    